/* global BigInt */

import React, { useState, useRef, useMemo, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import { Button, Box, Typography, Modal } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import withAuthProtection from "../route/authProtection.js";
import { fromZonedTime } from 'date-fns-tz';
import { createGlobalStyle } from "styled-components";
import { useSelector } from "react-redux";

const OutOfWorkingHoursStyle = createGlobalStyle`
  .rbc-timeslot-group.outside-working-hours {
    background-color: rgba(255,0,0,0.10);
  }

  .rbc-timeslot-group.outside-working-hours .rbc-time-slot {
    background-color: transparent;
  }

  .rbc-timeslot-group.outside-working-hours .rbc-label {
    color: #c62828;
  }
`;

const localizer = momentLocalizer(moment);

const Schedule = withAuthProtection(({
    start,
    end,
    onChange = () => {},
    onRangeSelected,
    onEventSelected = () => {},
    events,
    selectedUser,
    selectedUsers = []
  }) => {
  const calendarRef = useRef(null);
  const [currentView, setCurrentView] = useState("month");
  const [showOverlapError, setShowOverlapError] = useState(false);
  const [showLessonInPastError, setShowLessonInPastError] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [noCommonTimeslotsError, setNoCommonTimeslotsError] = useState(false);

  const activeUserSettings = useSelector(state => state.settings.settings);
  const user = JSON.parse(localStorage.getItem("user"));
  const TOTAL_SLOTS = 96n;

  const getWorkingHoursHexMask = useCallback((settings) => {
    return BigInt(`0x${settings.find(s => s.settingName === "working_hours")?.settingValue || 'ffffffffffffffffffffffff'}`);
  }, []);
  const getTimezone = useCallback((settings) => {
    return settings.find(s => s.settingName === "timezone")?.settingValue || "UTC";
  }, []);
  
  const currentUserSettings = useMemo(() => {
    return {
      currentUserTimezone: getTimezone(activeUserSettings),
      currentUserWorkingHours: getWorkingHoursHexMask(activeUserSettings)
    };
  }, [activeUserSettings, getTimezone, getWorkingHoursHexMask]);
  
  const timezoneSlotOffset = useCallback((fromZone, toZone) => {
      if (fromZone === toZone) return 0;
      const base = new Date(Date.UTC(2024, 0, 1, 0, 0, 0));
      const fromDate = fromZonedTime(base, fromZone);
      const toDate = fromZonedTime(base, toZone);
      const diffMinutes = (toDate - fromDate) / (1000 * 60);
      return Math.round(diffMinutes / 15);
    }, []);

  // const slotOffset = useMemo(() => {
  //   return timezoneSlotOffset(
  //     currentUserSettings.timezone ?? "UTC",
  //     currentUserSettings.currentUserTimezone
  //   );
  // }, [currentUserSettings, timezoneSlotOffset]);

  const shiftMaskByTimezone = useCallback((mask, fromZone, toZone) => {
    if (fromZone === toZone) return mask;
    const shift = BigInt(timezoneSlotOffset(fromZone, toZone));
    if (!shift) return mask;

    const s = BigInt((shift % TOTAL_SLOTS + TOTAL_SLOTS) % TOTAL_SLOTS);
    return (
      ((mask << s) | (mask >> (TOTAL_SLOTS - s))) &
      ((1n << TOTAL_SLOTS) - 1n)
    );
  }, [timezoneSlotOffset, TOTAL_SLOTS]);

  const selectedUserSettings = useMemo(() => {
    if (selectedUser) return [selectedUser.settings];
    return selectedUsers.map(u => u.settings);
  }, [selectedUser, selectedUsers]);

  const workingHours = useMemo(() => {
    let currentUserMask = currentUserSettings.currentUserWorkingHours;

    for (const userSettings of selectedUserSettings) {
      const userMask = getWorkingHoursHexMask(userSettings);
      const userTimezone = getTimezone(userSettings);
      const shiftedUserMask = shiftMaskByTimezone(
        userMask,
        userTimezone,
        currentUserSettings.currentUserTimezone
      );
      currentUserMask &= shiftedUserMask;
    }
    return currentUserMask;
  }, [currentUserSettings, selectedUserSettings, getTimezone, shiftMaskByTimezone, getWorkingHoursHexMask]);

  const workingSlots = useMemo(() => {
    const arr = new Uint8Array(96);
    let mask = workingHours;
    for (let i = 0; i < 96; i++) {
      arr[i] = Number(mask & 1n);
      mask >>= 1n;
    }
    return arr;
  }, [workingHours]);

  const handleSelectSlot = (selectInfo) => {
    console.log("Slot selected: ", selectInfo);
    const { start, end } = selectInfo;

    if (onRangeSelected) {
      const isNewInPast = start < new Date() || end < new Date();
      if (isNewInPast) {
        setShowLessonInPastError(true);
        return;
      }
      
      const isOverlapping = events.some(evt => start < evt.end && end > evt.start);
      if (isOverlapping) {
        setShowOverlapError(true);
        return;
      }

      onRangeSelected({ start, end });
    }
    onChange({ start, end });
  };

  const handleSelectEvent = (event) => {
    console.log("Event selected: ", event);
    onEventSelected(event);
  };

  const handleDrillDown = (date, drillView) => {
    if (workingHours === 0n) {
      setNoCommonTimeslotsError(true);
      return;
    }
    const targetView = drillView || "day";
    console.log("DrillDown to", targetView, "date:", date);
    setSelectedDate(date);
    setCurrentView(targetView);
  };

  const handleNavigate = (date, _) => {
    setSelectedDate(date);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const slotPropGetter = useCallback((date) => {
    if (!date) return {};

    let slot = (date.getHours() << 2) + (date.getMinutes() >> 4);
    //slot += slotOffset;
  
    if (slot < 0) slot += 96;
    if (slot >= 96) slot -= 96;
  
    if (!workingSlots[slot]) {
      return {
        style: {
          backgroundColor: "rgba(255,0,0,0.1)",
          color: "red",
          borderTop: "none",
          borderBottom: "none"
        }
      };
    }
  
    return {};
  }, [workingSlots]);

  const eventStyleGetter = (event) => {
    let backgroundColor = "#ccc";
    let opacity = 1;
    let fontColor = "white";

    if (!event.uid || !event.forSelectedUser) {
      backgroundColor = "#999";
      opacity = 0.3;
    } else {
      const { status } = event;
      const userType = user?.type;

      // Determine color based on status and user type:
      // * orange:      #e67e22 - current user should react
      const orange =     "#e67e22";
      // * blue:        #3498db - waiting for the other party
      const blue =       "#3498db";
      // * dark green:  #25854d - both parties agreed - upcoming lesson
      const darkGreen =  "#25854d";
      // * light green: #cdff7d - completed lesson
      const lightGreen = "#cdff7d";
      // * purple:      #ba3fbf - billed lesson
      const purple =     "#ba3fbf";
      // * yellow:      #fae740 - cancelled lesson
      const yellow =     "#fae740";

      if (["confirmed", "submitted"].includes(status)) {
        backgroundColor = darkGreen;
      } else if (["rescheduled", "requested"].includes(status)) {
        backgroundColor = userType === "student" ? blue : orange;
      } else if (["proposed", "moved"].includes(status)) {
        backgroundColor = userType === "student" ? orange : blue;
      } else if (["cancelled", "rejected"].includes(status)) {
        backgroundColor = yellow;
        fontColor = "black";
      } else if (status === "finished") {
        backgroundColor = lightGreen;
        fontColor = "black";
      } else if (status === "billed") {
        backgroundColor = purple;
      }
    }

    return {
      style: {
        backgroundColor,
        opacity,
        borderRadius: "0px",
        border: "none",
        color: fontColor,
        fontWeight: 500,
        padding: "1px 2px",
        display: "block",
      },
    };
  };

  function CustomToolbar({ label, onNavigate, onView }) {
    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Button
          onClick={() => onNavigate("PREV")}
          sx={{ minWidth: 48, left: 0 }}
        >
          <ArrowBackIos fontSize="large" />
        </Button>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight="bold">
            {label}
          </Typography>

          <Button variant="outlined" size="small" onClick={() => onNavigate("TODAY")}>
            Today
          </Button>

          <Button size="small" onClick={() => onView("month")}>Month</Button>
          <Button size="small" onClick={() => onView("week")}>Week</Button>
          <Button size="small" onClick={() => onView("day")}>Day</Button>
          <Button size="small" onClick={() => onView("agenda")}>Agenda</Button>
        </Box>

        <Button
          onClick={() => onNavigate("NEXT")}
          sx={{ minWidth: 48, right: 0 }}
        >
          <ArrowForwardIos fontSize="large" />
        </Button>
      </Box>
    );
  }

  return (
    <>
    <OutOfWorkingHoursStyle />
    <div>
      <style>
        {`
        .rbc-month-view .rbc-day-bg {
          cursor: pointer;
        }
        .rbc-time-view .rbc-day-bg {
          cursor: pointer;
        }
        .rbc-slot-selection,
        .rbc-time-slot {
          cursor: pointer;
        }
      `}
      </style>

      <div ref={calendarRef}>
        <Calendar
          localizer={localizer}
          events={events}
          step={15}
          timeslots={4}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600, width: 850 }}
          selectable
          onSelectSlot={(slotInfo) => {
            if (currentView === "month") {
              handleDrillDown(slotInfo.start, "day");
            } else {
              handleSelectSlot(slotInfo);
            }
          }}
          onSelectEvent={handleSelectEvent}
          view={currentView}
          onView={handleViewChange}
          date={selectedDate}
          onNavigate={handleNavigate}
          drilldownView="day"
          onDrillDown={handleDrillDown}
          components={{
            toolbar: CustomToolbar,
          }}
          eventPropGetter={eventStyleGetter}
          slotPropGetter={slotPropGetter}
        />
      </div>
    </div>

    <Modal open={showOverlapError || showLessonInPastError || noCommonTimeslotsError} onClose={() => { 
      setShowOverlapError(false);
      setShowLessonInPastError(false);
      setNoCommonTimeslotsError(false);
    }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          minWidth: 300,
          textAlign: "center"
        }}
      >
        { showOverlapError ?
         'Selected time slot is occupied. Please choose another time.' :
          showLessonInPastError ?
         'Selected time slot is in the past. Please choose a future time.' :
         'No common working hours found. Please check your and the other party\'s working hours settings.'}
      </Box>
    </Modal>

    </>
  );
});

export default Schedule;
